import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import NeilMethodSection from "@/components/NeilMethodSection";
import TechMedia from "@/components/TechMedia";
import HumanoidTabs from "@/components/HumanoidTabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Cpu,
  Activity,
  HeartPulse,
  Building2,
  Home,
  Users,
  Bell,
  Stethoscope,
  UtensilsCrossed,
  ClipboardList,
  Sparkles,
} from "lucide-react";

import heroC1 from "@/assets/humanoid-healthcare/hh-0790.jpeg.asset.json";
import designC1 from "@/assets/humanoid-healthcare/hh-0791.jpeg.asset.json";
import computeC1 from "@/assets/humanoid-healthcare/hh-0792.jpeg.asset.json";
import motionC1 from "@/assets/humanoid-healthcare/hh-0793.jpeg.asset.json";
import safetyC1 from "@/assets/humanoid-healthcare/hh-0794.jpeg.asset.json";
import customization from "@/assets/humanoid-healthcare/hh-0795.jpeg.asset.json";
import sensorsWalker from "@/assets/humanoid-healthcare/hh-0796.jpeg.asset.json";
import specsIndustrial from "@/assets/humanoid-healthcare/hh-0789.jpeg.asset.json";
import coAgent from "@/assets/humanoid-healthcare/hh-0788.jpeg.asset.json";
import walkAssist from "@/assets/humanoid-healthcare/hh-0801.jpeg.asset.json";
import smartChair from "@/assets/humanoid-healthcare/hh-0797.jpeg.asset.json";
import communityCare from "@/assets/humanoid-healthcare/hh-community-care.png.asset.json";
import seniorAi from "@/assets/humanoid-healthcare/hh-0798.jpeg.asset.json";
import residentialCare from "@/assets/humanoid-healthcare/hh-0800.jpeg.asset.json";



const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

/**
 * INTERNAL SOURCE-OF-TRUTH (not rendered publicly):
 * Platform data below is sourced from current official manufacturer product and
 * healthcare-solution documentation. Customer-facing copy stays manufacturer-neutral.
 * Last Specification Verification Date: 2026-08-16
 */
const SPEC_VERIFICATION_DATE = "2026-08-16";

const CARE_SETTINGS = [
  {
    icon: Home,
    title: "Home & Independent Living Support",
    body:
      "Connected safety, monitoring and intelligent-assistance technologies can support aging-friendly home environments.",
  },
  {
    icon: Users,
    title: "Community Care",
    body:
      "Integrated technologies can support community-based senior services, monitoring and communications.",
  },
  {
    icon: Building2,
    title: "Residential & Senior Living",
    body:
      "Connected robotics and automation can support staff workflows, facility operations and care environments.",
  },
];

const SCENARIOS = [
  {
    icon: HeartPulse,
    title: "Health Management",
    body: "Coordinated collection and review of health indicators through connected devices and service workflows.",
  },
  {
    icon: Sparkles,
    title: "Spiritual & Social Well-Being",
    body: "Engagement, communication and social-connection programs supported by interactive technology.",
  },
  {
    icon: UtensilsCrossed,
    title: "Nutrition & Diet",
    body: "Operational support for dietary planning, meal service coordination and related record keeping.",
  },
  {
    icon: Stethoscope,
    title: "Daily Nursing Care",
    body: "Support for routine care operations, scheduling and documentation around daily service delivery.",
  },
  {
    icon: ClipboardList,
    title: "Operations Management",
    body: "Potential integration of intelligent automation, monitoring and robotic systems into facility workflows.",
  },
  {
    icon: Activity,
    title: "Medical Rehabilitation",
    body:
      "Emerging robotics and intelligent systems may support rehabilitation environments where clinically and technically appropriate.",
  },
  {
    icon: Bell,
    title: "Safety Assurance",
    body: "Connected sensing, alerting and monitoring technologies designed to support safer care environments.",
  },
];

const DEVICE_CATEGORIES = [
  {
    name: "Smart Device Series",
    items: [
      "Pull-cord alarm",
      "Body sensor",
      "Audible and visual alarm",
      "Fire sensor",
      "Water-immersion sensor",
      "Smart button",
      "Smart gateway",
    ],
  },
  {
    name: "Safeguard Series",
    items: ["Base station", "Smart bracelet", "Label", "Badge"],
  },
  {
    name: "Monitoring Technology",
    items: [
      "Physical-sign monitoring device",
      "All-in-one physical examination machine",
      "Health-indicator collection device",
    ],
  },
  {
    name: "Health Management",
    items: ["AI healthcare service sensor"],
  },
  {
    name: "Fall-Prevention Management",
    items: ["Connected fall-prevention sensing and alerting as a solution category"],
  },
];

const FIT_AREAS = [
  { title: "Reception & Wayfinding", status: "Currently supported" },
  { title: "Visitor Interaction", status: "Currently supported" },
  { title: "Information Assistance", status: "Currently supported" },
  { title: "Facility Support", status: "Potential / Emerging Application" },
  { title: "Service Workflows", status: "Potential / Emerging Application" },
  { title: "Human-Robot Interaction", status: "Currently supported" },
  { title: "Research & Rehabilitation Environments", status: "Potential / Emerging Application" },
  { title: "Senior Engagement", status: "Potential / Emerging Application" },
];

const SAFETY_CARDS = [
  {
    title: "Motion Control Safety",
    points: ["Compliance force control", "Anti-fall protection", "Gentle, stable human-robot contact"],
  },
  {
    title: "Navigation Safety",
    points: [
      "Route planning with active risk avoidance",
      "Millisecond obstacle recognition and emergency evasion",
      "Adaptive gait adjustment and multi-stability control",
    ],
  },
  {
    title: "Electronics & Battery Safety",
    points: ["Multi-level emergency power-off protection", "Automotive-grade battery"],
  },
  {
    title: "Network & System Security",
    points: [
      "Independent safety main controller with fail-safe protection",
      "Local offline operation to protect user privacy and command security",
    ],
  },
];

const RAAS_STEPS = [
  "Discovery",
  "Site Assessment",
  "Workflow Design",
  "Security / IT Review",
  "Pilot",
  "Training",
  "Deployment",
  "Support",
  "Performance Review",
  "Multi-Site Expansion",
];

const ORG_TYPES = [
  "Hospitals",
  "Senior-living communities",
  "Skilled nursing facilities",
  "Assisted living",
  "Rehabilitation centers",
  "Healthcare campuses",
  "Medical offices",
  "Health systems",
];

const COMPLIANCE_ITEMS = [
  "Network review",
  "Data architecture",
  "Access controls",
  "Site safety",
  "Workflow assessment",
  "Human interaction review",
  "Emergency-stop functionality where present",
  "Deployment testing",
  "Staff training",
  "Escalation procedures",
];

const ECOSYSTEM = [
  { label: "Humanoid Robotics", to: "/robots/humanoid" },
  { label: "Delivery Robotics", to: "/robots" },
  { label: "Autonomous Cleaning", to: "/robots" },
  { label: "AI Receptionist", to: "/ai-workforce" },
  { label: "AI Scheduling", to: "/ai-workforce" },
  { label: "Workflow Automation", to: "/ai-automation" },
  { label: "Connected Safety Technology", to: "/healthcare" },
];

const SectionHeading = ({
  eyebrow,
  title,
  copy,
}: {
  eyebrow?: string;
  title: string;
  copy?: string;
}) => (
  <div className="max-w-3xl">
    {eyebrow && (
      <Badge className="mb-4 bg-accent/15 text-accent border border-accent/30">{eyebrow}</Badge>
    )}
    <h2 className="font-orbitron text-2xl md:text-4xl font-bold leading-tight">{title}</h2>
    {copy && <p className="mt-4 text-muted-foreground">{copy}</p>}
  </div>
);

const HumanoidHealthcare = () => (
  <>
    <SEO
      title="Humanoid Robotics for Healthcare & Senior Care | Neil AI"
      description="Explore how Neil AI combines humanoid robotics, connected care technology, AI automation and Robotics-as-a-Service for healthcare and senior-living environments."
      keywords="humanoid robotics healthcare, senior care automation, senior living robotics, healthcare robotics as a service, connected care technology"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="mx-auto w-full max-w-7xl px-[clamp(1rem,5vw,1.5rem)]">
        <HumanoidTabs />

        {/* HERO */}
        <section className="mt-6 grid gap-8 rounded-3xl border border-[hsl(var(--line))] glass-effect p-6 md:p-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
              Healthcare & Senior Care Automation
            </Badge>
            <h1 className="font-orbitron text-[clamp(1.75rem,6vw,3rem)] font-bold leading-tight">
              Humanoid Robotics for Healthcare & Senior Care
            </h1>
            <p className="mt-5 text-muted-foreground md:text-lg">
              Intelligent robotics and connected automation designed to support safer, more responsive
              and more efficient care environments.
            </p>
            <p className="mt-4 text-sm text-muted-foreground">
              Neil AI helps healthcare organizations, senior-living communities and care providers
              evaluate how humanoid robotics, intelligent sensing, AI automation and connected
              technologies can support operational workflows and the evolving care environment.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button asChild size="lg" className="rounded-full w-full sm:w-auto">
                <Link to="/neil-assessment">
                  Request a Healthcare Automation Assessment{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full w-full sm:w-auto">
                <a href="#capabilities">Explore Humanoid Capabilities</a>
              </Button>
            </div>
            <p className="mt-5 text-xs text-muted-foreground">
              Capabilities and availability vary by platform, application and deployment environment.
              Final recommendations follow the N.E.I.L.™ Assessment.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-[hsl(var(--line))] bg-white/5">
            <img
              src={heroC1.url}
              alt="Full-size humanoid service robot platform in motion"
              className="h-full w-full object-cover"
            />
          </div>
        </section>

        {/* CARE SETTINGS */}
        <section className="mt-16">
          <SectionHeading
            eyebrow="Solution Architecture"
            title="Smart Care Across Home, Community and Residential Settings"
            copy="Neil AI evaluates connected care technology across the three environments where senior care actually happens."
          />
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {CARE_SETTINGS.map((c) => (
              <Card key={c.title} className="glass-effect border-[hsl(var(--line))] p-6">
                <c.icon className="h-6 w-6 text-primary" />
                <h3 className="mt-4 font-orbitron text-base font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
              </Card>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Neil AI does not promise clinical outcomes. Technology is evaluated around operational and
            support workflows.
          </p>
        </section>

        {/* SEVEN SCENARIOS */}
        <section className="mt-16">
          <SectionHeading
            eyebrow="Application Scenarios"
            title="A Connected Care Ecosystem"
            copy="Seven operational areas where connected care technology, sensing and automation are commonly evaluated."
          />
          <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {SCENARIOS.map((s) => (
              <Card key={s.title} className="glass-effect border-[hsl(var(--line))] p-6">
                <s.icon className="h-5 w-5 text-accent" />
                <h3 className="mt-4 font-orbitron text-sm font-bold uppercase tracking-wide">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* CAPABILITIES */}
        <section id="capabilities" className="mt-20 scroll-mt-40">
          <SectionHeading
            eyebrow="Platform Capabilities"
            title="Verified Humanoid Capabilities"
            copy="Every capability below reflects current official platform documentation for the specific platform shown. Specifications are not shared between platforms."
          />

          {/* Perception */}
          <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={sensorsWalker.url}
              alt="Humanoid sensor architecture diagram showing camera, microphone, speaker, IMU, emergency stop and power switch placement"
              caption="Sensor architecture diagram supplied by the platform manufacturer. Product markings appear as manufactured."
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Perception & Interaction</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                The industrial humanoid platform integrates a multi-camera and audio sensing stack for
                omnidirectional awareness and human interaction.
              </p>
              <ul className="mt-4 space-y-2">
                {[
                  "2× fish-eye cameras, 1× binocular camera, 1× monocular camera",
                  "1× LCD display for on-robot interaction",
                  "2× speakers and a 4-microphone array",
                  "High-precision IMU (torso and waist)",
                  "Physical emergency-stop button and power switch",
                  "Dexterous hands available as an option",
                ].map((i) => (
                  <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Human-scale mobility */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="lg:order-2">
              <TechMedia
                src={designC1.url}
                alt="Full-size service humanoid full-body design showing joint articulation and walking posture"
              />
            </div>
            <div className="lg:order-1">
              <h3 className="font-orbitron text-xl font-bold">Human-Scale Mobility</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Specifications below apply to the full-size humanoid service platform only.
              </p>
              <dl className="mt-5 grid grid-cols-2 gap-4">
                {[
                  ["Degrees of freedom", "53 DOF"],
                  ["Height", "164 cm"],
                  ["Weight", "55 kg"],
                  ["Speed", "≥ 3 m/s"],
                  ["Motion", "Walking, running, dancing"],
                  ["Styling", "Easy outfit replacement"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-[hsl(var(--line))] p-4">
                    <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt>
                    <dd className="mt-1 text-sm font-semibold">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Embodied AI */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={computeC1.url}
              alt="Embodied AI and computing architecture diagram for the full-size humanoid service platform"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Embodied AI & Computing</h3>
              <ul className="mt-4 space-y-2">
                {[
                  "Multimodal large-model deployment with local offline operation",
                  "Custom knowledge base and spatiotemporal memory; cloud-compatible",
                  "3D autonomous navigation: real-time 3D mapping, full-body obstacle avoidance, millisecond-level AEB, centimeter-level low-obstacle detection",
                  "Vision-Language-Navigation / Vision-Language-Action interaction",
                  "Full-body trajectory tracking with dynamic balance on one or both feet",
                  "NVIDIA® Jetson Thor™ computing, 2070 TFLOPS (FP4)",
                  "Robot operating system application framework",
                ].map((i) => (
                  <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Autonomy is task- and environment-specific. Performance is validated during pilot.
              </p>
            </div>
          </div>

          {/* Safety */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="lg:order-2">
              <TechMedia
                src={safetyC1.url}
                alt="Safety architecture diagram covering motion control, navigation, electronics and network security"
              />
            </div>
            <div className="lg:order-1">
              <h3 className="font-orbitron text-xl font-bold">Safety-by-Design</h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {SAFETY_CARDS.map((s) => (
                  <Card key={s.title} className="glass-effect border-[hsl(var(--line))] p-4">
                    <ShieldCheck className="h-4 w-4 text-accent" />
                    <h4 className="mt-2 text-sm font-bold">{s.title}</h4>
                    <ul className="mt-2 space-y-1">
                      {s.points.map((p) => (
                        <li key={p} className="text-xs text-muted-foreground">
                          • {p}
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Motion control */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={motionC1.url}
              alt="Full-size humanoid demonstrating dynamic balance alongside dancers in a studio"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Advanced Motion Control</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                A built-in motion library supports dynamic multi-modal movement and customizable motion
                settings. Choreographed movement is a demonstration of balance and whole-body control
                research — not the commercial value proposition.
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                In enterprise environments, the same control stack matters for stable walking on varied
                flooring, safe movement around people, recovery from disturbance, and predictable
                behavior in corridors, lobbies and elevators.
              </p>
            </div>
          </div>

          {/* Customization */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="lg:order-2">
              <TechMedia
                src={customization.url}
                alt="Humanoid platforms shown in a range of service uniforms and outfits"
              />
            </div>
            <div className="lg:order-1">
              <h3 className="font-orbitron text-xl font-bold">Built for Different Environments</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Standard interfaces and a comprehensive SDK enable flexible customization for diverse
                application scenarios, and the platform supports easy outfit replacement for versatile
                styling by environment.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Appearance configurations shown are illustrative. Not every outfit or application shown
                is currently commercially available; configurations are confirmed per deployment.
              </p>
            </div>
          </div>

          {/* Co-agent */}
          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={coAgent.url}
              alt="Industrial agent architecture diagram showing perception, reasoning, planning and action"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Intelligent Task Understanding</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                The platform's industrial agent architecture connects an active behavior chain —
                perception → reasoning → task planning → action — to core cognitive elements including
                multimodal reasoning models, memory management, chain-of-thought and task-specific
                models, running on an end-to-end multimodal large model within the robot operating
                system application framework.
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-2">
                {["Intention understanding", "Task planning", "Tool use", "Anomaly detection & handling"].map(
                  (i) => (
                    <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                      <Cpu className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{i}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>
        </section>

        {/* ASSISTIVE MOBILITY PLATFORMS */}
        <section id="assistive-mobility" className="mt-20 scroll-mt-40">
          <SectionHeading
            eyebrow="Assistive Mobility"
            title="Mobility & Walking Assistance Platforms"
            copy="Beyond humanoids, Neil AI evaluates purpose-built assistive mobility platforms that support resident independence and reduce physical strain on care staff."
          />
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Card className="glass-effect overflow-hidden border-[hsl(var(--line))]">
              <img
                src={walkAssist.url}
                alt="Walking assistance robot with support handles and interactive display"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="p-6">
                <Badge variant="outline" className="text-[10px]">Potential / Emerging</Badge>
                <h3 className="mt-3 font-orbitron text-lg font-bold">Walking Assistance Platform</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  A self-balancing mobility aid with powered support handles, sit-to-stand assistance and
                  guided walking routines — designed to help residents move safely between rooms, therapy
                  areas and common spaces with fewer manual transfers.
                </p>
              </div>
            </Card>
            <Card className="glass-effect overflow-hidden border-[hsl(var(--line))]">
              <img
                src={smartChair.url}
                alt="Smart autonomous wheelchair platform on a paved outdoor path"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="p-6">
                <Badge variant="outline" className="text-[10px]">Potential / Emerging</Badge>
                <h3 className="mt-3 font-orbitron text-lg font-bold">Smart Mobility Chair</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  An intelligent powered chair with obstacle awareness, assisted navigation and all-terrain
                  wheels for indoor and outdoor use — supporting scheduled movement, appointments and
                  campus travel without a dedicated escort for every trip.
                </p>
              </div>
            </Card>
          </div>
          <div className="mt-6 grid gap-8 rounded-3xl border border-[hsl(var(--line))] glass-effect p-6 md:p-10 lg:grid-cols-2 lg:items-center">
            <img
              src={communityCare.url}
              alt="Care professional assisting a senior resident using a smart mobility chair outdoors"
              loading="lazy"
              className="w-full rounded-2xl object-cover"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Community Care, Extended Outdoors</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Assistive mobility extends care beyond the building — grounds, gardens, day programs and
                community outings — while caregivers stay focused on companionship and clinical judgment
                rather than lifting and pushing.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Assistive mobility devices are not medical devices unless explicitly certified. Neil AI does
                not provide clinical care, diagnosis or treatment. Device availability, certification and
                suitability are confirmed per deployment.
              </p>
            </div>
          </div>
        </section>

        {/* SMART ELDER CARE ECOSYSTEM */}
        <section className="mt-20">
          <SectionHeading
            eyebrow="Connected Ecosystem"
            title="Smart Elder Care Architecture"
            copy="Robotics only delivers value inside a coordinated system. These reference architectures show how scenarios, cloud platforms and connected devices work together across home, community and residential care."
          />
          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <TechMedia
              src={seniorAi.url}
              alt="Reference diagram: AI technology in senior care across government, enterprise and family programs"
            />
            <TechMedia
              src={residentialCare.url}
              alt="Reference diagram: seven care scenarios, cloud computing platform and five core technologies"
            />
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Reference architecture shown for illustration. Neil AI configures scope, integrations and data
            handling per facility and applicable privacy requirements.
          </p>
        </section>

        {/* CONTINUOUS OPERATION — moved to /robots/humanoid */}




        {/* WHERE HUMANOIDS MAY FIT */}
        <section className="mt-20">
          <SectionHeading
            eyebrow="Scope of Application"
            title="Where Humanoids May Fit"
            copy="Humanoid platforms do not replace nurses, CNAs, therapists or clinicians. Neil AI evaluates them against operational and service workflows only."
          />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {FIT_AREAS.map((f) => (
              <Card key={f.title} className="glass-effect border-[hsl(var(--line))] p-5">
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    f.status === "Currently supported" ? "text-primary border-primary/40" : ""
                  }`}
                >
                  {f.status}
                </Badge>
                <h3 className="mt-3 text-sm font-bold">{f.title}</h3>
              </Card>
            ))}
          </div>
        </section>

        {/* CONNECTED TECH */}
        <section className="mt-20">
          <SectionHeading
            eyebrow="Connected Smart-Care Technology"
            title="More Than a Humanoid."
            copy="A complete healthcare automation strategy can combine robotics, sensing, AI and connected devices."
          />
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {DEVICE_CATEGORIES.map((d) => (
              <Card key={d.name} className="glass-effect border-[hsl(var(--line))] p-6">
                <h3 className="font-orbitron text-sm font-bold uppercase tracking-wide">{d.name}</h3>
                <ul className="mt-3 space-y-1.5">
                  {d.items.map((i) => (
                    <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Neil AI does not stock every device category listed. Contact Neil AI for configuration and
            availability.
          </p>
        </section>

        {/* WORKFORCE */}
        <section className="mt-20 rounded-3xl border border-[hsl(var(--line))] glass-effect p-6 md:p-12">
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold max-w-3xl leading-tight">
            Support the People Who Provide Care.
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Neil AI's goal is not to replace the human element of healthcare. Automation should be
            evaluated around repetitive, operational and support workflows so professionals can spend
            more time on work requiring judgment, empathy and human connection.
          </p>
        </section>

        {/* RaaS */}
        <section className="mt-20">
          <SectionHeading
            eyebrow="Healthcare RaaS"
            title="Healthcare Robotics-as-a-Service"
            copy="A structured lifecycle from discovery through multi-site expansion, run by Neil AI."
          />
          <ol className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {RAAS_STEPS.map((s, i) => (
              <li
                key={s}
                className="rounded-2xl border border-[hsl(var(--line))] glass-effect p-4"
              >
                <span className="font-orbitron text-xs text-accent">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-1 text-sm font-semibold">{s}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex flex-wrap gap-2">
            {ORG_TYPES.map((o) => (
              <Badge key={o} variant="outline" className="text-[11px]">
                {o}
              </Badge>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            RaaS availability depends on platform, application, deployment environment and commercial
            agreement.
          </p>
          <div className="mt-6">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/raas">
                Explore Robotics-as-a-Service <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* COMPLIANCE */}
        <section className="mt-20">
          <SectionHeading
            eyebrow="Safety & Compliance"
            title="Designed for Serious Environments."
            copy="Every healthcare deployment goes through a structured technical, safety and operational review before go-live."
          />
          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {COMPLIANCE_ITEMS.map((c) => (
              <div
                key={c}
                className="flex gap-2 rounded-xl border border-[hsl(var(--line))] p-4 text-sm text-muted-foreground"
              >
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                <span>{c}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 max-w-3xl text-xs text-muted-foreground">
            Neil AI does not represent any robot or connected system as HIPAA certified, FDA approved,
            medical device certified, SOC 2 certified or NIST compliant. Certification status is
            confirmed in writing per product and per system during procurement review.
          </p>
          <div className="mt-6">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/enterprise">Review Enterprise Security & Procurement</Link>
            </Button>
          </div>
        </section>

        {/* SPEC ACCURACY / S2 */}
        <section className="mt-20 grid gap-8 lg:grid-cols-2 lg:items-center">
          <TechMedia
            src={specsIndustrial.url}
            alt="Industrial humanoid product specification diagram showing front and rear component callouts"
            caption={`Industrial humanoid platform. Specifications are maintained per platform and never shared across models. Last specification verification: ${SPEC_VERIFICATION_DATE}.`}
          />
          <div>
            <Badge variant="outline" className="text-[11px]">
              Industrial platform — not a care platform
            </Badge>
            <h3 className="mt-3 font-orbitron text-xl font-bold">
              Designed for Extended Industrial Operation
            </h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Neil AI's industrial humanoid class uses real-time battery monitoring, dual-battery
              dynamic power management and autonomous battery swapping in under three minutes to
              sustain continuous production operation.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              This platform is positioned for manufacturing and logistics environments. It is not
              recommended for elderly care simply because it is a humanoid. Healthcare and senior-living
              organizations should evaluate the commercial and service humanoid classes instead.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild size="sm" variant="outline" className="rounded-full">
                <Link to="/robots/humanoid">View Service & Commercial Humanoids</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* ECOSYSTEM CROSS-SELL */}
        <section className="mt-20 rounded-3xl border border-[hsl(var(--line))] glass-effect p-6 md:p-12">
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold leading-tight">
            Build the Autonomous Care Environment.
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {ECOSYSTEM.map((e, i) => (
              <span key={e.label} className="flex items-center gap-2">
                <Link
                  to={e.to}
                  className="rounded-full border border-[hsl(var(--line))] px-4 py-2 text-xs font-semibold hover:border-primary/50 hover:text-primary transition-colors"
                >
                  {e.label}
                </Link>
                {i < ECOSYSTEM.length - 1 && <span className="text-accent">•</span>}
              </span>
            ))}
          </div>
          <p className="mt-6 font-orbitron text-lg font-bold text-primary">
            = One Healthcare Automation Strategy
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-full w-full sm:w-auto">
              <Link to="/neil-assessment">
                Design My Healthcare Automation Blueprint <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full w-full sm:w-auto">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Talk to a Healthcare Specialist
              </a>
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <Link to="/healthcare" className="underline hover:text-primary">
              Healthcare industry solutions
            </Link>
            <Link to="/ai-workforce" className="underline hover:text-primary">
              AI workforce
            </Link>
            <Link to="/neil-method" className="underline hover:text-primary">
              The N.E.I.L.™ Method
            </Link>
          </div>
        </section>
      </div>
    </main>

    <div className="px-[clamp(1rem,5vw,1.5rem)]">
      <NeilMethodSection
        contextLabel="Every Healthcare Deployment Starts With N.E.I.L.™"
        contextCopy="Name the operational problem. Evaluate resident and patient areas, halls, elevators, doors, floor plans, connectivity, traffic and safety restrictions. Itemize staffing, workflows, task frequency, technology and equipment. Lock the verified operating baseline — then move to automation blueprint, pilot, deployment, RaaS, optimization and scale."
      />
    </div>

    <Footer />
  </>
);

export default HumanoidHealthcare;
